import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserGroupIcon,
  MapIcon,
  BookOpenIcon,
  InformationCircleIcon,
  SparklesIcon,
  PaperAirplaneIcon,
  ArrowDownTrayIcon,
  LinkIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import MobileLayout from '../../components/MobileLayout';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

type Tab = 'overview' | 'characters' | 'outline' | 'chapters' | 'threads';

interface Story {
  id: string;
  title: string;
  format: string;
  genres: string[];
  tone: string | null;
  pov: string | null;
  targetLength: string | null;
  status: string;
  wordCount: number;
  characters: any[];
  chapters: any[];
  outline: any | null;
  createdAt: string;
  updatedAt: string;
}

interface PlotThread {
  id: string;
  title: string;
  description: string | null;
  status: string;
  introducedChapterId: string | null;
  resolvedChapterId: string | null;
  characterIds: string[] | null;
}

interface Scene {
  id: string;
  chapterId: string;
  summary: string | null;
  povCharacterId: string | null;
  emotionalArc: string | null;
  keyMoments: string | null;
  sortOrder: number;
}

const statusColors: Record<string, string> = {
  planned: 'bg-blue-500/20 text-blue-400',
  in_progress: 'bg-yellow-500/20 text-yellow-400',
  draft: 'bg-green-500/20 text-green-400',
  revised: 'bg-purple-500/20 text-purple-400',
  final: 'bg-emerald-500/20 text-emerald-400',
};

const threadStatusBadge: Record<string, { icon: string; color: string }> = {
  open: { icon: '🔴', color: 'bg-red-500/20 text-red-400' },
  developing: { icon: '🟡', color: 'bg-yellow-500/20 text-yellow-400' },
  resolved: { icon: '✅', color: 'bg-green-500/20 text-green-400' },
};

const targetLengthWords: Record<string, number> = {
  short: 5000,
  medium: 20000,
  novel: 60000,
  epic: 100000,
};

const StoryWorkspace: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [story, setStory] = useState<Story | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [loading, setLoading] = useState(true);

  // Outline state
  const [beginning, setBeginning] = useState('');
  const [middle, setMiddle] = useState('');
  const [end, setEnd] = useState('');
  const [outlineDirty, setOutlineDirty] = useState(false);

  // Brainstorm
  const [brainstormPrompt, setBrainstormPrompt] = useState('');
  const [brainstormMessages, setBrainstormMessages] = useState<
    Array<{ role: 'user' | 'ai'; text: string }>
  >([]);
  const [brainstorming, setBrainstorming] = useState(false);

  // Character modal
  const [showCharForm, setShowCharForm] = useState(false);
  const [editingChar, setEditingChar] = useState<any>(null);
  const [charName, setCharName] = useState('');
  const [charRole, setCharRole] = useState('supporting');

  // Plot threads
  const [threads, setThreads] = useState<PlotThread[]>([]);
  const [showThreadForm, setShowThreadForm] = useState(false);
  const [editingThread, setEditingThread] = useState<PlotThread | null>(null);
  const [threadTitle, setThreadTitle] = useState('');
  const [threadDescription, setThreadDescription] = useState('');
  const [threadStatus, setThreadStatus] = useState('open');

  // Scenes (per chapter)
  const [expandedChapter, setExpandedChapter] = useState<string | null>(null);
  const [chapterScenes, setChapterScenes] = useState<Record<string, Scene[]>>({});
  const [showSceneForm, setShowSceneForm] = useState<string | null>(null);
  const [editingScene, setEditingScene] = useState<Scene | null>(null);
  const [sceneSummary, setSceneSummary] = useState('');
  const [scenePov, setScenePov] = useState('');
  const [sceneArc, setSceneArc] = useState('');

  // Export
  const [exporting, setExporting] = useState(false);

  const fetchStory = useCallback(async () => {
    try {
      const res = await axios.get(`/stories/${id}`);
      setStory(res.data);
      if (res.data.outline) {
        setBeginning(res.data.outline.beginning || '');
        setMiddle(res.data.outline.middle || '');
        setEnd(res.data.outline.end || '');
      }
    } catch {
      toast.error(t('stories.loadError', 'Failed to load story'));
      navigate('/stories');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  const fetchThreads = useCallback(async () => {
    try {
      const res = await axios.get(`/stories/${id}/threads`);
      setThreads(res.data);
    } catch {
      /* ignore */
    }
  }, [id]);

  const fetchScenes = useCallback(
    async (chapterId: string) => {
      try {
        const res = await axios.get(`/stories/${id}/chapters/${chapterId}/scenes`);
        setChapterScenes((prev) => ({ ...prev, [chapterId]: res.data }));
      } catch {
        /* ignore */
      }
    },
    [id],
  );

  useEffect(() => {
    fetchStory();
    fetchThreads();
  }, [fetchStory, fetchThreads]);

  const saveOutline = async () => {
    try {
      await axios.put(`/stories/${id}/outline`, { beginning, middle, end });
      setOutlineDirty(false);
      toast.success(t('stories.outlineSaved', 'Outline saved'));
    } catch {
      toast.error(t('stories.saveError', 'Failed to save'));
    }
  };

  const addChapter = async () => {
    try {
      const sortOrder = (story?.chapters?.length || 0) + 1;
      const res = await axios.post(`/stories/${id}/chapters`, {
        title: `Chapter ${sortOrder}`,
        sortOrder,
      });
      navigate(`/stories/${id}/chapters/${res.data.id}`);
    } catch {
      toast.error(t('stories.createError', 'Failed to create'));
    }
  };

  const saveCharacter = async () => {
    if (!charName.trim()) return;
    try {
      if (editingChar) {
        await axios.patch(`/stories/${id}/characters/${editingChar.id}`, {
          name: charName,
          role: charRole,
        });
      } else {
        await axios.post(`/stories/${id}/characters`, { name: charName, role: charRole });
      }
      setShowCharForm(false);
      setEditingChar(null);
      setCharName('');
      setCharRole('supporting');
      fetchStory();
    } catch {
      toast.error(t('stories.saveError', 'Failed to save'));
    }
  };

  const deleteCharacter = async (characterId: string) => {
    try {
      await axios.delete(`/stories/${id}/characters/${characterId}`);
      fetchStory();
    } catch {
      toast.error(t('stories.deleteError', 'Failed to delete'));
    }
  };

  const deleteChapter = async (chapterId: string) => {
    try {
      await axios.delete(`/stories/${id}/chapters/${chapterId}`);
      fetchStory();
    } catch {
      toast.error(t('stories.deleteError', 'Failed to delete'));
    }
  };

  const sendBrainstorm = async () => {
    if (!brainstormPrompt.trim() || brainstorming) return;
    const prompt = brainstormPrompt.trim();
    setBrainstormPrompt('');
    setBrainstormMessages((prev) => [...prev, { role: 'user', text: prompt }]);
    setBrainstorming(true);
    try {
      const res = await axios.post(`/stories/${id}/ai/brainstorm`, { prompt });
      setBrainstormMessages((prev) => [...prev, { role: 'ai', text: res.data.content }]);
    } catch {
      toast.error(t('stories.ai.error', 'AI request failed'));
    } finally {
      setBrainstorming(false);
    }
  };

  // Plot thread handlers
  const saveThread = async () => {
    if (!threadTitle.trim()) return;
    try {
      if (editingThread) {
        await axios.patch(`/stories/${id}/threads/${editingThread.id}`, {
          title: threadTitle,
          description: threadDescription || undefined,
          status: threadStatus,
        });
      } else {
        await axios.post(`/stories/${id}/threads`, {
          title: threadTitle,
          description: threadDescription || undefined,
          status: threadStatus,
        });
      }
      setShowThreadForm(false);
      setEditingThread(null);
      setThreadTitle('');
      setThreadDescription('');
      setThreadStatus('open');
      fetchThreads();
    } catch {
      toast.error(t('stories.saveError', 'Failed to save'));
    }
  };

  const deleteThread = async (threadId: string) => {
    try {
      await axios.delete(`/stories/${id}/threads/${threadId}`);
      fetchThreads();
    } catch {
      toast.error(t('stories.deleteError', 'Failed to delete'));
    }
  };

  // Scene handlers
  const saveScene = async (chapterId: string) => {
    if (!sceneSummary.trim()) return;
    try {
      if (editingScene) {
        await axios.patch(`/stories/${id}/chapters/${chapterId}/scenes/${editingScene.id}`, {
          summary: sceneSummary,
          povCharacterId: scenePov || undefined,
          emotionalArc: sceneArc || undefined,
        });
      } else {
        await axios.post(`/stories/${id}/chapters/${chapterId}/scenes`, {
          summary: sceneSummary,
          povCharacterId: scenePov || undefined,
          emotionalArc: sceneArc || undefined,
        });
      }
      setShowSceneForm(null);
      setEditingScene(null);
      setSceneSummary('');
      setScenePov('');
      setSceneArc('');
      fetchScenes(chapterId);
    } catch {
      toast.error(t('stories.saveError', 'Failed to save'));
    }
  };

  const deleteScene = async (chapterId: string, sceneId: string) => {
    try {
      await axios.delete(`/stories/${id}/chapters/${chapterId}/scenes/${sceneId}`);
      fetchScenes(chapterId);
    } catch {
      toast.error(t('stories.deleteError', 'Failed to delete'));
    }
  };

  const moveScene = async (chapterId: string, sceneId: string, direction: -1 | 1) => {
    const scenes = chapterScenes[chapterId] || [];
    const idx = scenes.findIndex((s) => s.id === sceneId);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= scenes.length) return;
    const ids = scenes.map((s) => s.id);
    [ids[idx], ids[newIdx]] = [ids[newIdx], ids[idx]];
    try {
      await axios.put(`/stories/${id}/chapters/${chapterId}/scenes/reorder`, {
        sceneIds: ids,
      });
      fetchScenes(chapterId);
    } catch {
      toast.error(t('stories.saveError', 'Failed to save'));
    }
  };

  // Export handlers
  const downloadFile = async (format: string, filename: string, mimeType: string) => {
    setExporting(true);
    try {
      const res = await axios.get(`/stories/${id}/export/${format}`, {
        responseType: 'text',
      });
      const blob = new Blob([res.data], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('stories.exportError', 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const openHtmlExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`/stories/${id}/export/html`, { responseType: 'text' });
      const blob = new Blob([res.data], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      toast.error(t('stories.exportError', 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  if (loading || !story) {
    return (
      <MobileLayout showBackButton>
        <div className="flex-1 flex items-center justify-center">
          <div
            className="animate-spin rounded-full h-8 w-8 border-b-2"
            style={{ borderColor: 'var(--ds-accent, #8b5cf6)' }}
          />
        </div>
      </MobileLayout>
    );
  }

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    {
      key: 'overview',
      label: t('stories.tabs.overview', 'Overview'),
      icon: <InformationCircleIcon className="w-4 h-4" />,
    },
    {
      key: 'characters',
      label: t('stories.tabs.characters', 'Characters'),
      icon: <UserGroupIcon className="w-4 h-4" />,
    },
    {
      key: 'outline',
      label: t('stories.tabs.outline', 'Outline'),
      icon: <MapIcon className="w-4 h-4" />,
    },
    {
      key: 'chapters',
      label: t('stories.tabs.chapters', 'Chapters'),
      icon: <BookOpenIcon className="w-4 h-4" />,
    },
    {
      key: 'threads',
      label: t('stories.tabs.threads', 'Plot Threads'),
      icon: <LinkIcon className="w-4 h-4" />,
    },
  ];

  const totalWords =
    story.chapters?.reduce((sum: number, c: any) => sum + (c.wordCount || 0), 0) || 0;
  const targetWords = targetLengthWords[story.targetLength || ''] || 0;
  const progressPct =
    targetWords > 0 ? Math.min(100, Math.round((totalWords / targetWords) * 100)) : 0;

  return (
    <MobileLayout headerTitle={story.title} showBackButton>
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Tabs */}
        <div
          className="flex border-b px-2 overflow-x-auto"
          style={{ borderColor: 'var(--ds-border)' }}
        >
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                tab === t.key ? 'border-primary-500 text-primary-500' : 'border-transparent'
              }`}
              style={tab !== t.key ? { color: 'var(--ds-text-secondary)' } : {}}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Overview Tab */}
          {tab === 'overview' && (
            <div className="space-y-4">
              <div
                className="ds-card rounded-2xl p-5"
                style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
              >
                <h2
                  className="text-xl font-semibold mb-3"
                  style={{ color: 'var(--ds-text-primary)' }}
                >
                  {story.title}
                </h2>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span style={{ color: 'var(--ds-text-secondary)' }}>
                      {t('stories.formatLabel', 'Format')}:{' '}
                    </span>
                    <span style={{ color: 'var(--ds-text-primary)' }}>{story.format}</span>
                  </div>
                  <div>
                    <span style={{ color: 'var(--ds-text-secondary)' }}>
                      {t('stories.statusLabel', 'Status')}:{' '}
                    </span>
                    <span style={{ color: 'var(--ds-text-primary)' }}>{story.status}</span>
                  </div>
                  {story.tone && (
                    <div>
                      <span style={{ color: 'var(--ds-text-secondary)' }}>
                        {t('stories.toneLabel', 'Tone')}:{' '}
                      </span>
                      <span style={{ color: 'var(--ds-text-primary)' }}>{story.tone}</span>
                    </div>
                  )}
                  {story.pov && (
                    <div>
                      <span style={{ color: 'var(--ds-text-secondary)' }}>
                        {t('stories.povLabel', 'POV')}:{' '}
                      </span>
                      <span style={{ color: 'var(--ds-text-primary)' }}>{story.pov}</span>
                    </div>
                  )}
                </div>
                {story.genres?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {story.genres.map((g) => (
                      <span
                        key={g}
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{
                          background: 'var(--ds-border)',
                          color: 'var(--ds-text-secondary)',
                        }}
                      >
                        {g}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: t('stories.words', 'Words'), value: totalWords.toLocaleString() },
                  {
                    label: t('stories.tabs.characters', 'Characters'),
                    value: story.characters?.length || 0,
                  },
                  {
                    label: t('stories.tabs.chapters', 'Chapters'),
                    value: story.chapters?.length || 0,
                  },
                ].map((s) => (
                  <div
                    key={s.label}
                    className="ds-card rounded-xl p-4 text-center"
                    style={{
                      background: 'var(--ds-bg-card)',
                      border: '1px solid var(--ds-border)',
                    }}
                  >
                    <div className="text-2xl font-bold" style={{ color: 'var(--ds-text-primary)' }}>
                      {s.value}
                    </div>
                    <div className="text-xs mt-1" style={{ color: 'var(--ds-text-secondary)' }}>
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* Word count progress */}
              {targetWords > 0 && (
                <div
                  className="ds-card rounded-2xl p-5"
                  style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className="text-sm font-medium"
                      style={{ color: 'var(--ds-text-primary)' }}
                    >
                      {t('stories.progress', 'Progress')}
                    </span>
                    <span className="text-xs" style={{ color: 'var(--ds-text-secondary)' }}>
                      {totalWords.toLocaleString()} / {targetWords.toLocaleString()}{' '}
                      {t('stories.words', 'words')} ({progressPct}%)
                    </span>
                  </div>
                  <div
                    className="w-full h-3 rounded-full overflow-hidden"
                    style={{ background: 'var(--ds-border)' }}
                  >
                    <div
                      className="h-full rounded-full bg-primary-500 transition-all"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Export */}
              <div
                className="ds-card rounded-2xl p-5"
                style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <ArrowDownTrayIcon className="w-5 h-5 text-primary-400" />
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--ds-text-primary)' }}>
                    {t('stories.export.title', 'Export')}
                  </h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => downloadFile('markdown', `${story.title}.md`, 'text/markdown')}
                    disabled={exporting}
                    className="px-4 py-2 rounded-lg text-sm font-medium border transition-colors hover:border-primary-500 disabled:opacity-50"
                    style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                  >
                    {t('stories.export.markdown', 'Download Markdown')}
                  </button>
                  <button
                    onClick={() => downloadFile('text', `${story.title}.txt`, 'text/plain')}
                    disabled={exporting}
                    className="px-4 py-2 rounded-lg text-sm font-medium border transition-colors hover:border-primary-500 disabled:opacity-50"
                    style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                  >
                    {t('stories.export.text', 'Download Text')}
                  </button>
                  <button
                    onClick={openHtmlExport}
                    disabled={exporting}
                    className="px-4 py-2 rounded-lg text-sm font-medium border transition-colors hover:border-primary-500 disabled:opacity-50"
                    style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                  >
                    {t('stories.export.html', 'Export HTML')}
                  </button>
                  {story.format === 'screenplay' && (
                    <button
                      onClick={() =>
                        downloadFile('screenplay', `${story.title}.fountain`, 'text/plain')
                      }
                      disabled={exporting}
                      className="px-4 py-2 rounded-lg text-sm font-medium border transition-colors hover:border-primary-500 disabled:opacity-50"
                      style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                    >
                      {t('stories.export.screenplay', 'Download Screenplay')}
                    </button>
                  )}
                </div>
              </div>

              {/* Brainstorm with AI */}
              <div
                className="ds-card rounded-2xl p-5"
                style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <SparklesIcon className="w-5 h-5 text-primary-400" />
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--ds-text-primary)' }}>
                    {t('stories.ai.brainstormTitle', 'Brainstorm with AI')}
                  </h3>
                </div>

                {brainstormMessages.length > 0 && (
                  <div className="space-y-3 mb-3 max-h-64 overflow-y-auto">
                    {brainstormMessages.map((msg, i) => (
                      <div
                        key={i}
                        className={`text-sm rounded-xl px-4 py-3 ${msg.role === 'user' ? 'ml-8' : 'mr-4'}`}
                        style={{
                          background:
                            msg.role === 'user'
                              ? 'var(--ds-accent, #8b5cf6)' + '22'
                              : 'var(--ds-bg-primary)',
                          color: 'var(--ds-text-primary)',
                          border: msg.role === 'ai' ? '1px solid var(--ds-border)' : 'none',
                        }}
                      >
                        <div className="whitespace-pre-wrap">{msg.text}</div>
                      </div>
                    ))}
                    {brainstorming && (
                      <div className="flex items-center gap-2 px-4 py-2">
                        <div
                          className="animate-spin rounded-full h-4 w-4 border-b-2"
                          style={{ borderColor: 'var(--ds-accent, #8b5cf6)' }}
                        />
                        <span className="text-xs" style={{ color: 'var(--ds-text-secondary)' }}>
                          {t('stories.ai.thinking', 'Thinking...')}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={brainstormPrompt}
                    onChange={(e) => setBrainstormPrompt(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') sendBrainstorm();
                    }}
                    placeholder={t(
                      'stories.ai.brainstormPlaceholder',
                      'Ask about plot, characters, world-building...',
                    )}
                    className="flex-1 px-3 py-2 rounded-lg border bg-transparent text-sm"
                    style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                  />
                  <button
                    onClick={sendBrainstorm}
                    disabled={brainstorming || !brainstormPrompt.trim()}
                    className="p-2 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white transition-colors"
                  >
                    <PaperAirplaneIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Characters Tab */}
          {tab === 'characters' && (
            <div className="space-y-3">
              <button
                onClick={() => {
                  setEditingChar(null);
                  setCharName('');
                  setCharRole('supporting');
                  setShowCharForm(true);
                }}
                className="w-full py-3 border-2 border-dashed rounded-xl text-sm font-medium transition-colors hover:border-primary-500"
                style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-secondary)' }}
              >
                <PlusIcon className="w-4 h-4 inline mr-1" />
                {t('stories.addCharacter', 'Add Character')}
              </button>

              {showCharForm && (
                <div
                  className="ds-card rounded-xl p-4 space-y-3"
                  style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
                >
                  <input
                    type="text"
                    value={charName}
                    onChange={(e) => setCharName(e.target.value)}
                    placeholder={t('stories.characterName', 'Character name')}
                    className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm"
                    style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                  />
                  <select
                    value={charRole}
                    onChange={(e) => setCharRole(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm"
                    style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                  >
                    {[
                      'protagonist',
                      'antagonist',
                      'love_interest',
                      'mentor',
                      'sidekick',
                      'supporting',
                      'minor',
                    ].map((r) => (
                      <option key={r} value={r}>
                        {r.replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                  <div className="flex gap-2">
                    <button
                      onClick={saveCharacter}
                      className="flex-1 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium"
                    >
                      {editingChar ? t('common.save', 'Save') : t('common.add', 'Add')}
                    </button>
                    <button
                      onClick={() => setShowCharForm(false)}
                      className="px-4 py-2 rounded-lg text-sm"
                      style={{ color: 'var(--ds-text-secondary)' }}
                    >
                      {t('common.cancel', 'Cancel')}
                    </button>
                  </div>
                </div>
              )}

              {story.characters?.map((char) => (
                <div
                  key={char.id}
                  className="ds-card rounded-xl p-4 flex items-center justify-between"
                  style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
                >
                  <div>
                    <div className="font-medium" style={{ color: 'var(--ds-text-primary)' }}>
                      {char.name}
                    </div>
                    <div
                      className="text-xs capitalize"
                      style={{ color: 'var(--ds-text-secondary)' }}
                    >
                      {char.role?.replace('_', ' ')}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingChar(char);
                        setCharName(char.name);
                        setCharRole(char.role);
                        setShowCharForm(true);
                      }}
                    >
                      <PencilIcon
                        className="w-4 h-4"
                        style={{ color: 'var(--ds-text-secondary)' }}
                      />
                    </button>
                    <button onClick={() => deleteCharacter(char.id)}>
                      <TrashIcon className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </div>
              ))}

              {(!story.characters || story.characters.length === 0) && !showCharForm && (
                <p
                  className="text-center text-sm py-8"
                  style={{ color: 'var(--ds-text-secondary)' }}
                >
                  {t('stories.noCharacters', 'No characters yet. Add your first one!')}
                </p>
              )}
            </div>
          )}

          {/* Outline Tab */}
          {tab === 'outline' && (
            <div className="space-y-4">
              {[
                {
                  label: t('stories.outline.beginning', 'Beginning'),
                  value: beginning,
                  set: setBeginning,
                },
                { label: t('stories.outline.middle', 'Middle'), value: middle, set: setMiddle },
                { label: t('stories.outline.end', 'End'), value: end, set: setEnd },
              ].map((section) => (
                <div key={section.label}>
                  <label
                    className="block text-sm font-medium mb-1.5"
                    style={{ color: 'var(--ds-text-primary)' }}
                  >
                    {section.label}
                  </label>
                  <textarea
                    value={section.value}
                    onChange={(e) => {
                      section.set(e.target.value);
                      setOutlineDirty(true);
                    }}
                    rows={4}
                    className="w-full px-3 py-2 rounded-xl border bg-transparent text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                    style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                    placeholder={t('stories.outline.placeholder', 'Describe this section...')}
                  />
                </div>
              ))}
              {outlineDirty && (
                <button
                  onClick={saveOutline}
                  className="w-full py-3 bg-primary-500 text-white rounded-xl font-semibold"
                >
                  {t('stories.saveOutline', 'Save Outline')}
                </button>
              )}
            </div>
          )}

          {/* Chapters Tab */}
          {tab === 'chapters' && (
            <div className="space-y-3">
              <button
                onClick={addChapter}
                className="w-full py-3 border-2 border-dashed rounded-xl text-sm font-medium transition-colors hover:border-primary-500"
                style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-secondary)' }}
              >
                <PlusIcon className="w-4 h-4 inline mr-1" />
                {t('stories.addChapter', 'Add Chapter')}
              </button>

              {story.chapters
                ?.sort((a: any, b: any) => a.sortOrder - b.sortOrder)
                .map((chapter: any, idx: number) => (
                  <div key={chapter.id}>
                    <div
                      className="ds-card rounded-xl p-4 flex items-center justify-between cursor-pointer hover:scale-[1.01] transition-transform"
                      style={{
                        background: 'var(--ds-bg-card)',
                        border: '1px solid var(--ds-border)',
                      }}
                      onClick={() => navigate(`/stories/${id}/chapters/${chapter.id}`)}
                    >
                      <div className="flex-1 min-w-0">
                        <div
                          className="font-medium truncate"
                          style={{ color: 'var(--ds-text-primary)' }}
                        >
                          {chapter.title || `Chapter ${idx + 1}`}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className={`text-[11px] px-2 py-0.5 rounded-full ${statusColors[chapter.status] || ''}`}
                          >
                            {chapter.status?.replace('_', ' ')}
                          </span>
                          <span className="text-xs" style={{ color: 'var(--ds-text-secondary)' }}>
                            {chapter.wordCount?.toLocaleString() || 0} {t('stories.words', 'words')}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (expandedChapter === chapter.id) {
                              setExpandedChapter(null);
                            } else {
                              setExpandedChapter(chapter.id);
                              fetchScenes(chapter.id);
                            }
                          }}
                          className="p-1 rounded"
                          style={{ color: 'var(--ds-text-secondary)' }}
                          title={t('stories.scenes.toggle', 'Scenes')}
                        >
                          {expandedChapter === chapter.id ? (
                            <ChevronUpIcon className="w-4 h-4" />
                          ) : (
                            <ChevronDownIcon className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteChapter(chapter.id);
                          }}
                        >
                          <TrashIcon className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </div>

                    {/* Scenes panel */}
                    {expandedChapter === chapter.id && (
                      <div
                        className="ml-4 mt-1 space-y-2 border-l-2 pl-3 py-2"
                        style={{ borderColor: 'var(--ds-border)' }}
                      >
                        <div className="flex items-center justify-between">
                          <span
                            className="text-xs font-semibold"
                            style={{ color: 'var(--ds-text-secondary)' }}
                          >
                            {t('stories.scenes.title', 'Scenes')}
                          </span>
                          <button
                            onClick={() => {
                              setEditingScene(null);
                              setSceneSummary('');
                              setScenePov('');
                              setSceneArc('');
                              setShowSceneForm(chapter.id);
                            }}
                            className="text-xs text-primary-400 hover:text-primary-300"
                          >
                            <PlusIcon className="w-3 h-3 inline mr-0.5" />
                            {t('stories.scenes.add', 'Add')}
                          </button>
                        </div>

                        {showSceneForm === chapter.id && (
                          <div
                            className="rounded-lg p-3 space-y-2"
                            style={{
                              background: 'var(--ds-bg-card)',
                              border: '1px solid var(--ds-border)',
                            }}
                          >
                            <input
                              type="text"
                              value={sceneSummary}
                              onChange={(e) => setSceneSummary(e.target.value)}
                              placeholder={t(
                                'stories.scenes.summaryPlaceholder',
                                'Scene summary...',
                              )}
                              className="w-full px-2 py-1.5 rounded border bg-transparent text-xs"
                              style={{
                                borderColor: 'var(--ds-border)',
                                color: 'var(--ds-text-primary)',
                              }}
                            />
                            <select
                              value={scenePov}
                              onChange={(e) => setScenePov(e.target.value)}
                              className="w-full px-2 py-1.5 rounded border bg-transparent text-xs"
                              style={{
                                borderColor: 'var(--ds-border)',
                                color: 'var(--ds-text-secondary)',
                              }}
                            >
                              <option value="">
                                {t('stories.scenes.povSelect', '— POV Character —')}
                              </option>
                              {story.characters?.map((c: any) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </select>
                            <input
                              type="text"
                              value={sceneArc}
                              onChange={(e) => setSceneArc(e.target.value)}
                              placeholder={t('stories.scenes.arcPlaceholder', 'Emotional arc...')}
                              className="w-full px-2 py-1.5 rounded border bg-transparent text-xs"
                              style={{
                                borderColor: 'var(--ds-border)',
                                color: 'var(--ds-text-primary)',
                              }}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => saveScene(chapter.id)}
                                className="flex-1 py-1.5 bg-primary-500 text-white rounded text-xs font-medium"
                              >
                                {editingScene ? t('common.save', 'Save') : t('common.add', 'Add')}
                              </button>
                              <button
                                onClick={() => setShowSceneForm(null)}
                                className="px-3 py-1.5 rounded text-xs"
                                style={{ color: 'var(--ds-text-secondary)' }}
                              >
                                {t('common.cancel', 'Cancel')}
                              </button>
                            </div>
                          </div>
                        )}

                        {(chapterScenes[chapter.id] || []).map((scene) => (
                          <div
                            key={scene.id}
                            className="rounded-lg p-3 flex items-start justify-between"
                            style={{
                              background: 'var(--ds-bg-card)',
                              border: '1px solid var(--ds-border)',
                            }}
                          >
                            <div className="flex-1 min-w-0">
                              <div
                                className="text-xs font-medium truncate"
                                style={{ color: 'var(--ds-text-primary)' }}
                              >
                                {scene.summary || t('stories.scenes.untitled', 'Untitled scene')}
                              </div>
                              {scene.emotionalArc && (
                                <div
                                  className="text-[10px] mt-0.5"
                                  style={{ color: 'var(--ds-text-secondary)' }}
                                >
                                  {scene.emotionalArc}
                                </div>
                              )}
                              {scene.povCharacterId && (
                                <div
                                  className="text-[10px] mt-0.5"
                                  style={{ color: 'var(--ds-text-secondary)' }}
                                >
                                  POV:{' '}
                                  {story.characters?.find((c: any) => c.id === scene.povCharacterId)
                                    ?.name || scene.povCharacterId}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1 ml-2 shrink-0">
                              <button onClick={() => moveScene(chapter.id, scene.id, -1)}>
                                <ChevronUpIcon
                                  className="w-3 h-3"
                                  style={{ color: 'var(--ds-text-secondary)' }}
                                />
                              </button>
                              <button onClick={() => moveScene(chapter.id, scene.id, 1)}>
                                <ChevronDownIcon
                                  className="w-3 h-3"
                                  style={{ color: 'var(--ds-text-secondary)' }}
                                />
                              </button>
                              <button
                                onClick={() => {
                                  setEditingScene(scene);
                                  setSceneSummary(scene.summary || '');
                                  setScenePov(scene.povCharacterId || '');
                                  setSceneArc(scene.emotionalArc || '');
                                  setShowSceneForm(chapter.id);
                                }}
                              >
                                <PencilIcon
                                  className="w-3 h-3"
                                  style={{ color: 'var(--ds-text-secondary)' }}
                                />
                              </button>
                              <button onClick={() => deleteScene(chapter.id, scene.id)}>
                                <TrashIcon className="w-3 h-3 text-red-400" />
                              </button>
                            </div>
                          </div>
                        ))}

                        {(chapterScenes[chapter.id] || []).length === 0 &&
                          showSceneForm !== chapter.id && (
                            <p
                              className="text-[11px] py-2"
                              style={{ color: 'var(--ds-text-secondary)' }}
                            >
                              {t('stories.scenes.empty', 'No scenes yet')}
                            </p>
                          )}
                      </div>
                    )}
                  </div>
                ))}

              {(!story.chapters || story.chapters.length === 0) && (
                <p
                  className="text-center text-sm py-8"
                  style={{ color: 'var(--ds-text-secondary)' }}
                >
                  {t('stories.noChapters', 'No chapters yet. Add your first one!')}
                </p>
              )}
            </div>
          )}

          {/* Plot Threads Tab */}
          {tab === 'threads' && (
            <div className="space-y-3">
              <button
                onClick={() => {
                  setEditingThread(null);
                  setThreadTitle('');
                  setThreadDescription('');
                  setThreadStatus('open');
                  setShowThreadForm(true);
                }}
                className="w-full py-3 border-2 border-dashed rounded-xl text-sm font-medium transition-colors hover:border-primary-500"
                style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-secondary)' }}
              >
                <PlusIcon className="w-4 h-4 inline mr-1" />
                {t('stories.threads.add', 'Add Plot Thread')}
              </button>

              {showThreadForm && (
                <div
                  className="ds-card rounded-xl p-4 space-y-3"
                  style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
                >
                  <input
                    type="text"
                    value={threadTitle}
                    onChange={(e) => setThreadTitle(e.target.value)}
                    placeholder={t('stories.threads.titlePlaceholder', 'Thread title...')}
                    className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm"
                    style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                  />
                  <textarea
                    value={threadDescription}
                    onChange={(e) => setThreadDescription(e.target.value)}
                    placeholder={t('stories.threads.descriptionPlaceholder', 'Description...')}
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm resize-none"
                    style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                  />
                  <select
                    value={threadStatus}
                    onChange={(e) => setThreadStatus(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm"
                    style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                  >
                    <option value="open">{t('stories.threads.statusOpen', '🔴 Open')}</option>
                    <option value="developing">
                      {t('stories.threads.statusDeveloping', '🟡 Developing')}
                    </option>
                    <option value="resolved">
                      {t('stories.threads.statusResolved', '✅ Resolved')}
                    </option>
                  </select>
                  <div className="flex gap-2">
                    <button
                      onClick={saveThread}
                      className="flex-1 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium"
                    >
                      {editingThread ? t('common.save', 'Save') : t('common.add', 'Add')}
                    </button>
                    <button
                      onClick={() => setShowThreadForm(false)}
                      className="px-4 py-2 rounded-lg text-sm"
                      style={{ color: 'var(--ds-text-secondary)' }}
                    >
                      {t('common.cancel', 'Cancel')}
                    </button>
                  </div>
                </div>
              )}

              {threads.map((thread) => {
                const badge = threadStatusBadge[thread.status] || threadStatusBadge.open;
                return (
                  <div
                    key={thread.id}
                    className="ds-card rounded-xl p-4 flex items-start justify-between"
                    style={{
                      background: 'var(--ds-bg-card)',
                      border: '1px solid var(--ds-border)',
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${badge.color}`}>
                          {badge.icon} {thread.status}
                        </span>
                        <span
                          className="font-medium truncate"
                          style={{ color: 'var(--ds-text-primary)' }}
                        >
                          {thread.title}
                        </span>
                      </div>
                      {thread.description && (
                        <p
                          className="text-xs mt-1.5 line-clamp-2"
                          style={{ color: 'var(--ds-text-secondary)' }}
                        >
                          {thread.description}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 ml-2 shrink-0">
                      <button
                        onClick={() => {
                          setEditingThread(thread);
                          setThreadTitle(thread.title);
                          setThreadDescription(thread.description || '');
                          setThreadStatus(thread.status);
                          setShowThreadForm(true);
                        }}
                      >
                        <PencilIcon
                          className="w-4 h-4"
                          style={{ color: 'var(--ds-text-secondary)' }}
                        />
                      </button>
                      <button onClick={() => deleteThread(thread.id)}>
                        <TrashIcon className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                );
              })}

              {threads.length === 0 && !showThreadForm && (
                <p
                  className="text-center text-sm py-8"
                  style={{ color: 'var(--ds-text-secondary)' }}
                >
                  {t('stories.threads.empty', 'No plot threads yet. Track your story arcs!')}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </MobileLayout>
  );
};

export default StoryWorkspace;
